/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { Send, Loader, Mic, MicOff } from 'lucide-react';

interface RequestInputProps {
  title: string;
  description: string;
  examples: string[];
  value: string;
  isProcessing: boolean;
  isListening: boolean;
  lastResponse: string;
  speechSupported: boolean;
  placeholder?: string;
  'data-walkthrough'?: string;
  onValueChange: (value: string) => void;
  onProcess: () => void;
  onVoiceInput: () => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
}

export function RequestInput({
  title,
  description,
  examples,
  value,
  isProcessing,
  isListening,
  lastResponse,
  speechSupported,
  placeholder = "Describe what you want to do... (or use the voice button)",
  'data-walkthrough': dataWalkthrough,
  onValueChange,
  onProcess,
  onVoiceInput,
  onKeyPress
}: RequestInputProps) {
  return (
    <div 
      style={{
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '24px',
        background: '#f8f9fa'
      }}
      data-walkthrough={dataWalkthrough}
    >
      <h3 style={{ marginBottom: '16px', color: '#0066cc' }}>{title}</h3>
      <p style={{ marginBottom: '16px', color: '#666', fontSize: '14px' }}>
        {description}
        {examples.length > 0 && (
          <>
            {' '}Examples: {examples.map((ex, i) => (
              <span key={i}>
                "{ex}"{i < examples.length - 1 ? ', ' : ''}
              </span>
            ))}
          </>
        )}
        {speechSupported && (
          <><br /><strong>Voice input:</strong> Click the Voice button and speak your request. Works best in Chrome and Edge browsers.</>
        )}
        {!speechSupported && (
          <><br /><span style={{ color: '#dc3545' }}><strong>Note:</strong> Voice input is not supported in your browser. Try Chrome or Edge for voice features.</span></>
        )}
      </p>

      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
        <textarea
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          onKeyPress={onKeyPress}
          placeholder={placeholder}
          rows={3}
          disabled={isProcessing || isListening}
          style={{
            flex: 1,
            padding: '12px',
            border: '1px solid #ddd',
            borderRadius: '6px',
            fontSize: '14px',
            resize: 'vertical',
            minHeight: '80px'
          }}
        />

        {/* Voice input button */}
        {speechSupported && (
          <button
            onClick={onVoiceInput}
            disabled={isProcessing}
            className="btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minWidth: '80px',
              justifyContent: 'center',
              backgroundColor: isListening ? '#dc3545' : '#6c757d',
              borderColor: isListening ? '#dc3545' : '#6c757d'
            }}
            title={isListening ? 'Stop recording' : 'Start voice input'}
          >
            {isListening ? (
              <>
                <MicOff size={16} />
                Stop
              </>
            ) : (
              <>
                <Mic size={16} />
                Voice
              </>
            )}
          </button>
        )}

        <button
          onClick={onProcess}
          disabled={isProcessing || !value.trim() || isListening}
          className="btn"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            minWidth: '100px',
            justifyContent: 'center'
          }}
        >
          {isProcessing ? (
            <>
              <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
              Processing
            </>
          ) : (
            <>
              <Send size={16} />
              Process
            </>
          )}
        </button>
      </div>

      {/* Response display */}
      {lastResponse && (
        <div style={{
          marginTop: '16px',
          padding: '12px',
          border: '1px solid #ddd',
          borderRadius: '6px',
          background: lastResponse.startsWith('Error:') ? '#fff5f5' : '#f0f9ff',
          borderColor: lastResponse.startsWith('Error:') ? '#fed7d7' : '#bfdbfe'
        }}>
          <h4 style={{
            margin: '0 0 8px 0',
            fontSize: '14px',
            color: lastResponse.startsWith('Error:') ? '#c53030' : '#1e40af'
          }}>
            Response:
          </h4>
          <div style={{
            fontSize: '14px',
            color: lastResponse.startsWith('Error:') ? '#c53030' : '#374151',
            whiteSpace: 'pre-wrap'
          }}>
            {lastResponse}
          </div>
        </div>
      )}
    </div>
  );
}

